import type { UniversalModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import ButtonModifierBlock from "@/features/react-editor/components/modifiers/ButtonModifierBlock";
import ContainerModifierBlock from "@/features/react-editor/components/modifiers/ContainerModifierBlock";
import { useModifiers } from "@/features/react-editor/components/modifiers/hooks/useModifiers";
import ImageModifierBlock from "@/features/react-editor/components/modifiers/ImageModifierBlock";
import { useModifiersStore } from "@/features/react-editor/components/modifiers/store/ModifiersStoreContext";
import TextModifierBlock from "@/features/react-editor/components/modifiers/TextModifierBlock";
import IconButton from "@replo/design-system/components/button/IconButton";
import { Trash2, X } from "lucide-react";

const MODIFIER_BLOCKS = {
  text: {
    displayName: "Text",
    component: TextModifierBlock,
  },
  container: {
    displayName: "Container",
    component: ContainerModifierBlock,
  },
  button: {
    displayName: "Button",
    component: ButtonModifierBlock,
  },
  image: {
    displayName: "Image",
    component: ImageModifierBlock,
  },
  // TODO: Add video, etc. modifier blocks here
} as const;

export default function Modifiers() {
  const { selectedElement, actions: modifierActions } = useModifiersStore();

  const { applyModifier } = useModifiers();

  const handleModifiersChange = async (modifiers: UniversalModifierValue[]) => {
    for (const modifier of modifiers) {
      await applyModifier(modifier);
    }
  };

  const getModifierBlock = (elementType: string) => {
    return MODIFIER_BLOCKS[elementType as keyof typeof MODIFIER_BLOCKS];
  };

  const getElementDisplayName = (elementType: string): string => {
    const block = getModifierBlock(elementType);
    return (
      block?.displayName ??
      elementType.charAt(0).toUpperCase() + elementType.slice(1)
    );
  };

  const renderModifierComponent = () => {
    if (!selectedElement) {
      return null;
    }

    const block = getModifierBlock(selectedElement.elementType);

    if (block) {
      const ModifierComponent = block.component;
      return (
        <ModifierComponent
          onModifiersValueChange={(modifiers) => {
            void handleModifiersChange(modifiers);
          }}
        />
      );
    }

    // Fallback for unimplemented modifier types
    return (
      <div className="text-sm text-muted">
        Modifiers for {selectedElement.elementType} elements are not yet
        implemented.
      </div>
    );
  };

  return (
    <div className="relative flex max-h-full w-full p-4 flex-col gap-4 flex-start border-border rounded-xl bg-white border-0.5">
      <div className="flex items-center justify-between flex-shrink-0">
        <div className="flex items-center header-h4">
          {selectedElement
            ? getElementDisplayName(selectedElement.elementType)
            : "Selected element"}
        </div>
        <div className="flex items-center gap-3">
          <IconButton
            variant="tertiary"
            size="sm"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() => void applyModifier({ type: "delete" })}
          />
          <IconButton
            variant="tertiary"
            size="sm"
            icon={<X className="w-4 h-4" />}
            onClick={() => {
              modifierActions.setSelectedElement(null);
            }}
          />
        </div>
      </div>
      <div className="border-t-0.5 border-border -mx-4 flex-shrink-0" />
      <div
        className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overflow-x-hidden styled-scrollbar pr-2"
        style={{
          scrollbarGutter: "stable",
        }}
      >
        {renderModifierComponent()}
      </div>
    </div>
  );
}
